import React, { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { motion, AnimatePresence } from 'framer-motion'
import useTranslation from '../../hooks/useTranslation'

export default function Index() {
  const { t } = useTranslation()
  const [isDemoOpen, setIsDemoOpen] = useState(false)
  const { auth } = usePage().props

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <div className="text-2xl font-bold text-emerald-600 tracking-tight">LinguaClick</div>
        <div className="space-x-4">
          <a href="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</a>
          <a href="/session/new" className="text-gray-600 hover:text-gray-900 font-medium">{t('home.log_in')}</a>
          {auth?.user?.role === "admin" && (
            <Link href="/admin/texts" className="text-gray-600 hover:text-gray-900 font-medium">
              {t('home.admin')}
            </Link>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-8 py-12 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight leading-tight mb-6">
              {t('home.hero_title_prefix')} <span className="text-emerald-600">{t('home.hero_title_highlight')}</span>.
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg">
              {t('home.hero_description')}
            </p>
            <div className="flex gap-4">
              <Link 
                href="/texts" 
                className="bg-emerald-600 text-white px-8 py-4 rounded-xl text-lg font-bold shadow-lg hover:bg-emerald-700 transition-colors"
              >
                {t('home.start_reading')}
              </Link>
              <a 
                href="/session/new" 
                className="bg-white text-emerald-700 border border-emerald-200 px-8 py-4 rounded-xl text-lg font-bold shadow-sm hover:bg-emerald-50 transition-colors inline-block text-center"
              >
                {t('home.sign_up')}
              </a>
            </div>
          </motion.div>

          {/* Interactive Demo Preview */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-2xl blur opacity-30"></div>
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-6 border-b pb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="text-sm font-mono text-gray-400">{t('home.preview_reader_preview')}</div>
              </div>
              
              <div className="font-serif text-2xl leading-loose text-gray-800">
                <span>Le </span>
                <span className="cursor-pointer hover:bg-gray-100 px-1 rounded transition-colors">chat</span>
                <span> </span>
                <span className="relative inline-block">
                   <span className="px-1 rounded bg-amber-100 text-gray-900 font-medium">dort</span>
                   <div className="absolute left-1/2 -translate-x-1/2 -top-10 bg-white shadow-lg px-2 py-1 rounded text-sm text-emerald-700 font-bold whitespace-nowrap border border-gray-100">
                     sleeps
                     <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-white"></div>
                   </div>
                </span>
                <span> </span>
                <span>sur le tapis.</span>
              </div>

              <div className="mt-8 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {t('home.preview_topic')}: <span className="font-semibold text-gray-900">{t('home.preview_daily_life')}</span>
                </div>
                <div className="bg-rose-50 px-3 py-1 rounded-full text-rose-600 font-bold text-sm">
                  {t('home.preview_penalty')}: 1
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Feature Grid */}
      <section className="bg-white py-24 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <Feature 
              icon="📖" 
              title={t('home.feature_contextual_title')} 
              desc={t('home.feature_contextual_desc')} 
            />
            <Feature 
              icon="🎯" 
              title={t('home.feature_gamified_title')} 
              desc={t('home.feature_gamified_desc')} 
            />
            <Feature 
              icon="🧠" 
              title={t('home.feature_recall_title')} 
              desc={t('home.feature_recall_desc')} 
            />
          </div>
          
          {/* Try it Button */}
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setIsDemoOpen(!isDemoOpen)}
              className="bg-emerald-600 text-white px-8 py-4 rounded-xl text-lg font-bold shadow-lg hover:bg-emerald-700 transition-colors"
            >
              {isDemoOpen ? 'Close Demo' : 'Try it'}
            </button>
          </div>
        </div>
      </section>

      {/* Expandable Demo */}
      <AnimatePresence>
        {isDemoOpen && (
          <motion.section
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className="bg-gray-50 border-t border-gray-200 overflow-hidden"
          >
            <div className="max-w-4xl mx-auto px-8 py-12">
              <DemoTextReader />
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  )
}

function Feature({ icon, title, desc }) {
  return (
    <div className="flex flex-col items-start">
      <div className="text-4xl mb-4 bg-emerald-100 w-16 h-16 flex items-center justify-center rounded-2xl">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2 text-gray-900">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  )
}

function DemoTextReader() {
  const [revealedIds, setRevealedIds] = useState(new Set())
  const [penaltyCount, setPenaltyCount] = useState(0)

  // Sample German text about LinguaClick with tokenized data
  const sampleText = {
    title: "Über LinguaClick",
    topic: "About the App",
    tokens: [
      { id: 0, w: "LinguaClick", t: "LinguaClick", p: false },
      { id: 1, w: " ", t: " ", p: true },
      { id: 2, w: "ist", t: "is", p: false },
      { id: 3, w: " ", t: " ", p: true },
      { id: 4, w: "eine", t: "a", p: false },
      { id: 5, w: " ", t: " ", p: true },
      { id: 6, w: "innovative", t: "innovative", p: false },
      { id: 7, w: " ", t: " ", p: true },
      { id: 8, w: "Sprachlernplattform", t: "language learning platform", p: false },
      { id: 9, w: ".", t: ".", p: true },
      { id: 10, w: " ", t: " ", p: true },
      { id: 11, w: "Sie", t: "It", p: false },
      { id: 12, w: " ", t: " ", p: true },
      { id: 13, w: "hilft", t: "helps", p: false },
      { id: 14, w: " ", t: " ", p: true },
      { id: 15, w: "dir", t: "you", p: false },
      { id: 16, w: ",", t: ",", p: true },
      { id: 17, w: " ", t: " ", p: true },
      { id: 18, w: "neue", t: "new", p: false },
      { id: 19, w: " ", t: " ", p: true },
      { id: 20, w: "Sprachen", t: "languages", p: false },
      { id: 21, w: " ", t: " ", p: true },
      { id: 22, w: "durch", t: "through", p: false },
      { id: 23, w: " ", t: " ", p: true },
      { id: 24, w: "Lesen", t: "reading", p: false },
      { id: 25, w: " ", t: " ", p: true },
      { id: 26, w: "zu", t: "to", p: false },
      { id: 27, w: " ", t: " ", p: true },
      { id: 28, w: "lernen", t: "learn", p: false },
      { id: 29, w: ".", t: ".", p: true },
      { id: 30, w: " ", t: " ", p: true },
      { id: 31, w: "Klicke", t: "Click", p: false },
      { id: 32, w: " ", t: " ", p: true },
      { id: 33, w: "auf", t: "on", p: false },
      { id: 34, w: " ", t: " ", p: true },
      { id: 35, w: "Wörter", t: "words", p: false },
      { id: 36, w: ",", t: ",", p: true },
      { id: 37, w: " ", t: " ", p: true },
      { id: 38, w: "um", t: "to", p: false },
      { id: 39, w: " ", t: " ", p: true },
      { id: 40, w: "Übersetzungen", t: "translations", p: false },
      { id: 41, w: " ", t: " ", p: true },
      { id: 42, w: "zu", t: "to", p: false },
      { id: 43, w: " ", t: " ", p: true },
      { id: 44, w: "sehen", t: "see", p: false },
      { id: 45, w: ".", t: ".", p: true },
      { id: 46, w: " ", t: " ", p: true },
      { id: 47, w: "Jede", t: "Each", p: false },
      { id: 48, w: " ", t: " ", p: true },
      { id: 49, w: "Sitzung", t: "session", p: false },
      { id: 50, w: " ", t: " ", p: true },
      { id: 51, w: "endet", t: "ends", p: false },
      { id: 52, w: " ", t: " ", p: true },
      { id: 53, w: "mit", t: "with", p: false },
      { id: 54, w: " ", t: " ", p: true },
      { id: 55, w: "einem", t: "a", p: false },
      { id: 56, w: " ", t: " ", p: true },
      { id: 57, w: "Quiz", t: "quiz", p: false },
      { id: 58, w: ",", t: ",", p: true },
      { id: 59, w: " ", t: " ", p: true },
      { id: 60, w: "um", t: "to", p: false },
      { id: 61, w: " ", t: " ", p: true },
      { id: 62, w: "dein", t: "your", p: false },
      { id: 63, w: " ", t: " ", p: true },
      { id: 64, w: "Verständnis", t: "understanding", p: false },
      { id: 65, w: " ", t: " ", p: true },
      { id: 66, w: "zu", t: "to", p: false },
      { id: 67, w: " ", t: " ", p: true },
      { id: 68, w: "testen", t: "test", p: false },
      { id: 69, w: ".", t: ".", p: true }
    ]
  }

  const handleTokenClick = (token) => {
    if (token.p) return
    if (revealedIds.has(token.id)) return

    const newSet = new Set(revealedIds)
    newSet.add(token.id)
    setRevealedIds(newSet)
    setPenaltyCount(p => p + 1)
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6 text-white">
        <h2 className="text-2xl font-bold mb-1">{sampleText.title}</h2>
        <p className="text-emerald-100 text-sm">{sampleText.topic}</p>
      </div>

      {/* Content */}
      <div className="p-8">
        {/* Stats Bar */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b">
          <div className="text-sm text-gray-500">
            Click on words to see translations
          </div>
          <div className="bg-rose-50 px-4 py-2 rounded-full text-rose-600 font-bold text-sm">
            Penalties: {penaltyCount}
          </div>
        </div>

        {/* Text Display */}
        <div className="text-xl md:text-2xl leading-loose font-serif text-gray-800 mb-8">
          {sampleText.tokens.map(token => (
            <DemoToken
              key={token.id}
              token={token}
              isRevealed={revealedIds.has(token.id)}
              onClick={() => handleTokenClick(token)}
            />
          ))}
        </div>

        {/* Info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
          <p className="font-semibold mb-1">💡 This is how LinguaClick works!</p>
          <p>Click on any German word above to see its English translation. Each click counts as a penalty - challenge yourself to read with fewer clicks!</p>
        </div>
      </div>
    </div>
  )
}

function DemoToken({ token, isRevealed, onClick }) {
  if (token.p) {
    return <span>{token.w}</span>
  }

  return (
    <span
      onClick={onClick}
      className={`relative inline-block cursor-pointer px-1 mx-0.5 rounded transition-all duration-200 select-none ${
        isRevealed
          ? 'bg-amber-100 text-gray-900'
          : 'hover:bg-gray-100 hover:text-gray-600'
      }`}
    >
      {token.w}
      <AnimatePresence>
        {isRevealed && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.9 }}
            animate={{ opacity: 1, y: -45, scale: 1 }}
            exit={{ opacity: 0 }}
            className="absolute left-1/2 -translate-x-1/2 bg-white border border-gray-200 shadow-xl px-3 py-1.5 rounded-lg text-sm font-sans whitespace-nowrap z-50 pointer-events-none text-emerald-700 font-bold"
          >
            {token.t}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-8 border-transparent border-t-white"></div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  )
}

